import React, { useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useProject, useUpdateProject, useDeleteProject } from '../hooks/useProjects';
import { usePermission, SETTINGS_TABS } from '../contexts/PermissionContext';
import ProjectStatusBadge from '../components/ProjectStatusBadge';
import TimeScopeDisplay from '../components/TimeScopeDisplay';
import ProjectVendorManagement from '../components/ProjectVendorManagement';
import ProjectFormsTab from '../components/project/ProjectFormsTab';
import ProjectResponsesTab from '../components/project/ProjectResponsesTab';
import ProjectMonitoringTab from '../components/project/ProjectMonitoringTab';
import { getTypeLabel, PROJECT_STATUSES } from '../services/projectsAPI';

// Tab 定義
const TABS = [
  { id: 'vendors', label: '供應商', icon: '🏢' },
  { id: 'forms', label: '問卷管理', icon: '📝' },
  { id: 'responses', label: '回覆追蹤', icon: '📊' },
  { id: 'monitoring', label: '專案監控', icon: '📈' },
];

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAccessSettingsTab } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hardDelete, setHardDelete] = useState(false);

  // 只有有權限標籤管理權限的使用者才能編輯/刪除專案
  const canEditProject = canAccessSettingsTab(SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT);

  // Tab 狀態（從 URL 參數讀取，預設為 vendors）
  const activeTab = searchParams.get('tab') || 'vendors';
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const { data, isLoading, error } = useProject(id, {
    include_stats: true,
  });

  const updateProject = useUpdateProject({
    onSuccess: () => {
      toast.success('專案已更新');
    },
    onError: (error) => {
      toast.error(`更新失敗: ${error.message}`);
    },
  });

  const deleteProject = useDeleteProject({
    onSuccess: () => {
      navigate('/projects');
    },
    onError: (error) => {
      toast.error(`刪除失敗: ${error.message}`);
    },
  });

  const handleStatusChange = (newStatus) => {
    if (confirm(`確定要將專案狀態變更為「${PROJECT_STATUSES.find(s => s.value === newStatus)?.label}」嗎？`)) {
      updateProject.mutate({ id, data: { status: newStatus } });
    }
  };

  const handleDelete = () => {
    deleteProject.mutate({ id, hard_delete: hardDelete });
    setShowDeleteConfirm(false);
    setHardDelete(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-purple-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">載入失敗</h3>
          <p className="text-red-600">{error.message}</p>
          <Link
            to="/projects"
            className="inline-block mt-4 text-purple-600 hover:text-purple-700"
          >
            ← 返回專案列表
          </Link>
        </div>
      </div>
    );
  }

  const project = data?.data;
  if (!project) return null;

  return (
    <div className="min-h-screen bg-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 返回按鈕 */}
        <Link
          to="/projects"
          className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          返回專案列表
        </Link>

        {/* 專案標題區域 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">
                  {project.name}
                </h1>
                <ProjectStatusBadge status={project.status} />
              </div>
              <p className="text-gray-500 font-mono text-sm">{project.code}</p>
            </div>

            {canEditProject && (
              <div className="flex gap-2">
                <Link
                  to={`/projects/${id}/edit`}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  編輯
                </Link>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  刪除
                </button>
              </div>
            )}
          </div>

          {/* 專案資訊 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">專案類型</p>
              <p className="text-base font-medium text-gray-900">
                {getTypeLabel(project.project_type)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">時間範圍</p>
              <TimeScopeDisplay timeScope={project.time_scope} showIcon={false} />
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">擁有者</p>
              <p className="text-base font-medium text-gray-900">
                {project.team?.owner || '未設定'}
              </p>
            </div>
          </div>

          {project.description && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2">專案描述</p>
              <p className="text-gray-900">{project.description}</p>
            </div>
          )}
        </div>

        {/* 狀態變更快捷按鈕 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="text-sm font-medium text-gray-700">變更狀態：</span>
            <div className="flex flex-wrap gap-2">
              {PROJECT_STATUSES.map((status) => (
                <button
                  key={status.value}
                  onClick={() => handleStatusChange(status.value)}
                  disabled={project.status === status.value || updateProject.isPending}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    project.status === status.value
                      ? 'bg-purple-600 text-white cursor-default'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 專案統計 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {project.stats?.total_vendors || 0}
            </div>
            <div className="text-sm text-gray-600">供應商</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {project.stats?.total_forms || 0}
            </div>
            <div className="text-sm text-gray-600">問卷</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {project.stats?.total_responses || 0}
            </div>
            <div className="text-sm text-gray-600">已回覆</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {project.stats?.completion_rate || 0}%
            </div>
            <div className="text-sm text-gray-600">完成率</div>
          </div>
        </div>

        {/* Tab 導航 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-4 px-6 text-center font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab 內容 */}
          <div className="p-0">
            {activeTab === 'vendors' && (
              <ProjectVendorManagement projectId={id} project={project} />
            )}
            {activeTab === 'forms' && (
              <ProjectFormsTab projectId={id} />
            )}
            {activeTab === 'responses' && (
              <ProjectResponsesTab projectId={id} />
            )}
            {activeTab === 'monitoring' && (
              <ProjectMonitoringTab projectId={id} />
            )}
          </div>
        </div>

        {/* 刪除確認對話框 */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowDeleteConfirm(false); setHardDelete(false); }}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  確認刪除專案
                </h3>
                <button onClick={() => { setShowDeleteConfirm(false); setHardDelete(false); }} className="p-1 hover:bg-gray-100 rounded transition">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-4">
                確定要刪除「{project.name}」嗎？
              </p>
              <label className="flex items-center gap-2 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hardDelete}
                  onChange={(e) => setHardDelete(e.target.checked)}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">
                  永久刪除（無法恢復）
                </span>
              </label>
              {hardDelete && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-700">
                    警告：永久刪除將無法恢復，所有相關資料將被徹底清除！
                  </p>
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setHardDelete(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteProject.isPending}
                  className={`px-4 py-2 text-white rounded-md text-sm font-medium disabled:opacity-50 ${
                    hardDelete ? 'bg-red-700 hover:bg-red-800' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {deleteProject.isPending ? '刪除中...' : (hardDelete ? '永久刪除' : '封存專案')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectDetail;
